import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Trophy, Copy, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

// ----------------------------------------------------------------
// Posted jackpot totals, on the dashboard.
// ----------------------------------------------------------------
// The Hold'em figure is republished on marketing assets every day, so it is
// surfaced here rather than only on the Cage Balance Sheet — whoever updates
// the assets should not have to go looking for it.
//
// It is a DERIVED figure (anchor + accrual since), not a stored one. Where a
// game has no anchor the card says so and shows NO number: a plausible wrong
// figure here would go straight onto a poster.
//
// Hidden entirely for users without cbs:view, and silent on a 403 rather than
// shouting at anyone who happens to land on the dashboard.
// ----------------------------------------------------------------

const GAME_LABEL = { HE: "Hold'em", BJ: 'Blackjack', TCP: 'Three Card', UTH: 'UTH' };
const SECONDARY = ['BJ', 'TCP', 'UTH'];

const fmtCents = (c) =>
  c === null || c === undefined
    ? '—'
    : `${c < 0 ? '-' : ''}$${Math.abs(Math.round(c) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export default function PostedJackpotCard() {
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [asOf, setAsOf] = useState(todayYmd());
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const date = todayYmd();
    try {
      const res = await api.get('/admin/cbs/jackpots/posted', { params: { date } });
      setGames(res.data.games || {});
      setAsOf(res.data.asOf || date);
    } catch (e) {
      // No cbs:view, the endpoint not deployed yet, or a server fault: take the
      // card away rather than leave an error where a published figure should
      // be. An unexpected status still goes to the console so it is findable.
      const status = e?.response?.status;
      if (status !== 401 && status !== 403 && status !== 404) {
        console.error('[PostedJackpotCard] could not load posted jackpots:', e?.message || e);
      }
      setHidden(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (hidden) return null;
  if (loading && !games) return null;

  const he = games && games.HE;

  const copy = async () => {
    if (!he || !he.anchored) return;
    try {
      await navigator.clipboard.writeText(fmtCents(he.postedCents));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — select the figure and copy it manually.');
    }
  };

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-white">Posted Jackpots</h2>
      <div className="rounded-xl border border-gray-700 bg-gray-800 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-300/80">
              <Trophy size={14} className="text-amber-400" />
              {(he && he.config && he.config.postedLabel) || "Texas Hold'em Jackpot"} · {asOf}
            </div>
            {he && he.anchored ? (
              <>
                <div className="mt-1 text-4xl font-bold tabular-nums text-amber-300">
                  {fmtCents(he.postedCents)}
                </div>
                {/* The arithmetic spelled out, so the figure can be checked
                    rather than trusted. */}
                <div className="mt-1 text-xs text-gray-400">
                  {fmtCents(he.anchor && he.anchor.cents)} on {he.anchor && he.anchor.date}
                  {he.anchor && he.anchor.source === 'reseed'
                    ? ' (re-seed after a hit)'
                    : ' (opening balance)'}
                  {he.accrual && he.accrual.days
                    ? ` + ${he.accrual.days} days = ${fmtCents(he.accrual.cents)}`
                    : ''}
                </div>
              </>
            ) : (
              <div className="mt-2 flex max-w-xl items-start gap-2 text-sm text-amber-200">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>
                  {(he && he.reason) || 'No posted figure is available yet.'}{' '}
                  <button
                    onClick={() => navigate('/cage-balance')}
                    className="underline hover:text-amber-100"
                  >
                    Set it on the Cage Balance Sheet
                  </button>
                  .
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {he && he.anchored && (
              <button
                onClick={copy}
                className="flex items-center gap-1 rounded-lg border border-amber-500/60 px-3 py-1.5 text-sm font-medium text-amber-200 hover:bg-amber-500/20"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="rounded-lg border border-gray-600 p-1.5 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* The funding position beside the promise. A negative gap under a
            mandatory daily accrual is the designed state — the house fronts the
            difference on a low-drop day — so it is stated plainly, not flagged. */}
        {he && he.anchored && (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-700 pt-3 text-xs">
            <span className="text-gray-500">
              Promised since the anchor{' '}
              <span className="tabular-nums text-gray-300">
                {fmtCents(he.accrual && he.accrual.cents)}
              </span>
            </span>
            <span className="text-gray-500">
              GL credited{' '}
              <span className="tabular-nums text-gray-300">{fmtCents(he.meterCreditedCents)}</span>
            </span>
            <span className="text-gray-500">
              Gap{' '}
              <span
                className={`tabular-nums ${
                  he.fundingGapCents < 0 ? 'text-amber-300' : 'text-emerald-300'
                }`}
              >
                {fmtCents(he.fundingGapCents)}
              </span>
            </span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-3">
          {SECONDARY.map((g) => {
            const p = games && games[g];
            return (
              <div key={g} className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
                <div className="text-xs text-gray-500">{GAME_LABEL[g]}</div>
                {p && p.anchored ? (
                  <div className="mt-0.5 text-lg font-semibold tabular-nums text-white">
                    {fmtCents(p.postedCents)}
                  </div>
                ) : (
                  <div className="mt-0.5 text-xs text-amber-300/80">not anchored</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
